import { setGrabbing } from "@/lib/pointerDnd";
import React, { useEffect, useState } from "react";
import { Reorder } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft, GripVertical, Loader2, Save, LayoutDashboard, MessagesSquare, Table2,
  FolderKanban, Ticket, FolderOpen, KanbanSquare, Bot, BookOpen, Crown, Candy,
  Inbox, Building2, ListChecks, CalendarClock, BarChart3, Users, Gauge, FolderLock, Code2, CalendarCheck, FileText,
  BellRing, Plus, Trash2, Mail, Bell, Pencil,
} from "lucide-react";

const CLIENT_META = {
  dashboard: { label: "Dashboard", icon: LayoutDashboard },
  inbox: { label: "Inbox", icon: Inbox },
  comms: { label: "Comms", icon: MessagesSquare },
  leads: { label: "Leads", icon: Table2 },
  reports: { label: "Reports", icon: FolderKanban },
  tickets: { label: "Tickets", icon: Ticket },
  files: { label: "Files", icon: FolderOpen },
  mdaas: { label: "MDaaS", icon: KanbanSquare },
  topo: { label: "TOPO", icon: Bot },
  knowledge: { label: "Knowledge Base", icon: BookOpen },
  forms: { label: "Forms", icon: FileText },
  cmoaas: { label: "CMOaaS", icon: Crown },
  internal: { label: "Internal", icon: FolderLock },
  snackwall: { label: "SnackWall", icon: Candy },
};
const CONSOLE_META = {
  inbox: { label: "Inbox", icon: Inbox },
  clients: { label: "Clients", icon: Building2 },
  projects: { label: "Projects", icon: KanbanSquare },
  tasks: { label: "Tasks", icon: ListChecks },
  scheduler: { label: "Scheduler", icon: CalendarCheck },
  workload: { label: "Workload", icon: Gauge },
  timesheet: { label: "Timesheet", icon: CalendarClock },
  insights: { label: "Insights", icon: BarChart3 },
  internal: { label: "Internal", icon: FolderLock },
  knowledge: { label: "Knowledge Base", icon: BookOpen },
  forms: { label: "Forms", icon: FileText },
  topo: { label: "AI Agents", icon: Bot },
  team: { label: "Team", icon: Users },
};

function ReorderList({ testid, order, setOrder, meta }) {
  return (
    <Reorder.Group axis="y" values={order} onReorder={setOrder} className="space-y-2" data-testid={testid}>
      {order.map((key) => {
        const m = meta[key];
        if (!m) return null;
        const Icon = m.icon;
        return (
          <Reorder.Item
            key={key}
            value={key}
            data-testid={`${testid}-item-${key}`}
            whileDrag={{ scale: 1.02, boxShadow: "0 12px 30px -12px rgba(114,63,255,0.35)", borderColor: "#723FFF", backgroundColor: "#F7F4FF" }}
            onDragStart={() => setGrabbing(true)} onDragEnd={() => setGrabbing(false)}
            className="flex items-center gap-3 rounded-2xl border border-[#EEEFEF] bg-white px-3.5 py-3 cursor-grab active:cursor-grabbing select-none"
          >
            <GripVertical className="h-4 w-4 text-[#c4c5cc] shrink-0" />
            <span className="h-8 w-8 rounded-lg bg-[#F6F6FD] text-[#5b2fd6] flex items-center justify-center shrink-0"><Icon className="h-[18px] w-[18px]" /></span>
            <span className="text-sm text-[#06060C] flex-1">{m.label}</span>
          </Reorder.Item>
        );
      })}
    </Reorder.Group>
  );
}

export function CustomCodeCard({ embedded }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [code, setCode] = useState({ custom_code_head: "", custom_code_body: "", custom_code_footer: "" });

  useEffect(() => {
    api.get("/agency/settings").then(({ data }) => {
      setCode({
        custom_code_head: data.custom_code_head || "",
        custom_code_body: data.custom_code_body || "",
        custom_code_footer: data.custom_code_footer || "",
      });
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail))).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/agency/settings", code);
      toast.success("Custom code saved — refresh any open pages to apply.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const fields = [
    { key: "custom_code_head", label: "Header code", hint: "Injected inside <head> on every page — meta tags, analytics, fonts." },
    { key: "custom_code_body", label: "Body code", hint: "Injected at the top of <body> — e.g. tag-manager noscript." },
    { key: "custom_code_footer", label: "Footer code", hint: "Injected before </body> — e.g. the Userback feedback widget." },
  ];

  return (
    <div className={embedded ? "" : "mt-12 pt-10 border-t border-[#EEEFEF]"} data-testid="custom-code-section">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-1">
        <div className="flex items-center gap-2">
          <span className="h-8 w-8 rounded-lg bg-[#F6F6FD] text-[#5b2fd6] flex items-center justify-center"><Code2 className="h-[18px] w-[18px]" /></span>
          <h2 className="font-display text-lg font-semibold text-[#06060C]">Custom code</h2>
        </div>
        <Button data-testid="custom-code-save" onClick={save} disabled={saving || loading} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> Save code</>}
        </Button>
      </div>
      <p className="text-xs text-[#9FA1A4] mb-6">Paste third-party scripts (Userback, analytics, chat widgets) to run across the entire portal — the console, every client workspace and the login screens. Scripts execute live. Only Super Admins see this. If a script ever breaks a page, add <code className="font-mono-data text-[#5b2fd6]">?nocustomcode=1</code> to the URL to load without custom code and fix it (<code className="font-mono-data text-[#5b2fd6]">?nocustomcode=0</code> re-enables).</p>
      {loading ? <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#c4c5cc]" /></div> : (
        <div className="space-y-6">
          {fields.map((f) => (
            <div key={f.key}>
              <label className="text-sm font-medium text-[#06060C] block mb-1">{f.label}</label>
              <p className="text-xs text-[#9FA1A4] mb-2">{f.hint}</p>
              <Textarea
                data-testid={`custom-code-${f.key}`}
                value={code[f.key]}
                onChange={(e) => setCode({ ...code, [f.key]: e.target.value })}
                spellCheck={false}
                rows={6}
                placeholder="<script> … </script>"
                className="bg-[#0d0d14] text-[#e6e6ef] border-[#EEEFEF] font-mono-data text-xs leading-relaxed resize-y placeholder:text-[#5a5a6e]"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const TRIGGER_OPTIONS = [
  { value: "before", label: "Before due date" },
  { value: "on_due", label: "On the due date" },
  { value: "overdue", label: "After due date (overdue)" },
];

const BLANK_RULE = { name: "", trigger_type: "overdue", offset_days: 1, message: "", email: true, in_app: true, active: true };

function ReminderEditor({ rule, onSave, onCancel, saving }) {
  const [r, setR] = useState(rule);
  const upd = (patch) => setR((p) => ({ ...p, ...patch }));
  return (
    <div className="rounded-2xl border border-[#d9ccff] bg-[#F8F5FF] p-4 space-y-3" data-testid="reminder-editor">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-[#06060C] block mb-1">Reminder name</label>
          <Input data-testid="reminder-name" value={r.name || ""} onChange={(e) => upd({ name: e.target.value })} placeholder="e.g. Overdue nudge" className="bg-white border-[#EEEFEF] h-9 text-sm" />
        </div>
        <div>
          <label className="text-xs font-medium text-[#06060C] block mb-1">When to send</label>
          <div className="flex items-center gap-2">
            <Select value={r.trigger_type} onValueChange={(v) => upd({ trigger_type: v, offset_days: v === "on_due" ? 0 : Math.max(1, r.offset_days || 1) })}>
              <SelectTrigger data-testid="reminder-trigger" className="bg-white border-[#EEEFEF] h-9 text-sm flex-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-[#EEEFEF]">{TRIGGER_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
            {r.trigger_type !== "on_due" && (
              <div className="flex items-center gap-1.5 shrink-0">
                <Input data-testid="reminder-offset" type="number" min={1} max={60} value={r.offset_days} onChange={(e) => upd({ offset_days: Math.max(1, Math.min(60, parseInt(e.target.value || "1", 10))) })} className="bg-white border-[#EEEFEF] h-9 text-sm w-16" />
                <span className="text-xs text-[#595D62]">day(s)</span>
              </div>
            )}
          </div>
        </div>
      </div>
      <div>
        <label className="text-xs font-medium text-[#06060C] block mb-1">Message</label>
        <Textarea data-testid="reminder-message" value={r.message} onChange={(e) => upd({ message: e.target.value })} rows={3} placeholder="Hi {name}, a reminder that “{task}” is due {due_date}." className="bg-white border-[#EEEFEF] text-sm resize-y" />
        <p className="text-[11px] text-[#9FA1A4] mt-1">Placeholders: <code className="text-[#5b2fd6]">{"{name}"}</code> <code className="text-[#5b2fd6]">{"{task}"}</code> <code className="text-[#5b2fd6]">{"{due_date}"}</code> <code className="text-[#5b2fd6]">{"{client}"}</code></p>
      </div>
      <div className="flex items-center gap-5">
        <label className="flex items-center gap-2 text-sm text-[#06060C] cursor-pointer"><Checkbox data-testid="reminder-email" checked={r.email} onCheckedChange={(v) => upd({ email: !!v })} /> <Mail className="h-3.5 w-3.5 text-[#9FA1A4]" /> Email</label>
        <label className="flex items-center gap-2 text-sm text-[#06060C] cursor-pointer"><Checkbox data-testid="reminder-inapp" checked={r.in_app} onCheckedChange={(v) => upd({ in_app: !!v })} /> <Bell className="h-3.5 w-3.5 text-[#9FA1A4]" /> In-app</label>
      </div>
      <div className="flex items-center gap-2 pt-1">
        <Button data-testid="reminder-save" size="sm" onClick={() => onSave(r)} disabled={saving} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save reminder"}</Button>
        <Button size="sm" variant="ghost" onClick={onCancel} className="text-[#595D62] rounded-full">Cancel</Button>
      </div>
    </div>
  );
}

export function ClientNotificationsCard({ embedded }) {
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState([]);
  const [editing, setEditing] = useState(null); // rule object or 'new'
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/agency/client-reminders").then(({ data }) => setRules(data.reminders || [])).catch((e) => toast.error(formatApiError(e.response?.data?.detail))).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const save = async (r) => {
    if (!r.message?.trim()) { toast.error("Add a reminder message."); return; }
    if (!r.email && !r.in_app) { toast.error("Choose at least one delivery channel."); return; }
    setSaving(true);
    try {
      if (r.id) await api.put(`/agency/client-reminders/${r.id}`, r);
      else await api.post("/agency/client-reminders", r);
      toast.success("Reminder saved"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const toggleActive = async (rule) => { try { await api.put(`/agency/client-reminders/${rule.id}`, { ...rule, active: !rule.active }); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const del = async (id) => { try { await api.delete(`/agency/client-reminders/${id}`); toast.success("Reminder removed"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <div className={embedded ? "" : "mt-12 pt-10 border-t border-[#EEEFEF]"} data-testid="client-notifications-section">
      <div className="flex items-center gap-2 mb-1">
        <span className="h-8 w-8 rounded-lg bg-[#F6F6FD] text-[#5b2fd6] flex items-center justify-center"><BellRing className="h-[18px] w-[18px]" /></span>
        <h2 className="font-display text-lg font-semibold text-[#06060C]">Client Notifications</h2>
      </div>
      <p className="text-xs text-[#9FA1A4] mb-6 max-w-2xl">Task reminders for work assigned to clients. These apply platform-wide across every client workspace — the assigned client teammate is reminded by email and/or an in-app notification. Each reminder fires once per task.</p>

      {loading ? <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#c4c5cc]" /></div> : (
        <div className="space-y-3">
          {rules.map((rule) => (
            editing?.id === rule.id ? (
              <ReminderEditor key={rule.id} rule={editing} onSave={save} onCancel={() => setEditing(null)} saving={saving} />
            ) : (
              <div key={rule.id} className="flex items-center gap-3 rounded-2xl border border-[#EEEFEF] bg-white px-4 py-3" data-testid={`reminder-row-${rule.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-[#06060C]">{rule.name}</span>
                    <span className="text-[11px] rounded-full bg-[#F6F6FD] text-[#5b2fd6] px-2 py-0.5">{rule.trigger_label}</span>
                    {rule.email && <span className="inline-flex items-center gap-1 text-[11px] text-[#9FA1A4]"><Mail className="h-3 w-3" /> Email</span>}
                    {rule.in_app && <span className="inline-flex items-center gap-1 text-[11px] text-[#9FA1A4]"><Bell className="h-3 w-3" /> In-app</span>}
                  </div>
                  <p className="text-xs text-[#595D62] mt-1 truncate">{rule.message}</p>
                </div>
                <Switch data-testid={`reminder-active-${rule.id}`} checked={rule.active} onCheckedChange={() => toggleActive(rule)} />
                <button data-testid={`reminder-edit-${rule.id}`} onClick={() => setEditing(rule)} className="text-[#9FA1A4] hover:text-[#5b2fd6]"><Pencil className="h-4 w-4" /></button>
                <button data-testid={`reminder-delete-${rule.id}`} onClick={() => del(rule.id)} className="text-[#9FA1A4] hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            )
          ))}
          {editing === "new" ? (
            <ReminderEditor rule={BLANK_RULE} onSave={save} onCancel={() => setEditing(null)} saving={saving} />
          ) : (
            <Button data-testid="reminder-add" variant="outline" onClick={() => setEditing("new")} className="border-dashed border-[#d9ccff] text-[#5b2fd6] hover:bg-[#F8F5FF] rounded-full"><Plus className="h-4 w-4 mr-1.5" /> Add reminder</Button>
          )}
        </div>
      )}
    </div>
  );
}

export function NavLayoutSettings({ onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [consoleOrder, setConsoleOrder] = useState([]);
  const [clientOrder, setClientOrder] = useState([]);

  useEffect(() => {
    api.get("/agency/nav-layout").then(({ data }) => {
      setConsoleOrder(data.console_order || []);
      setClientOrder(data.client_order || []);
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail))).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/agency/nav-layout", { console_order: consoleOrder, client_order: clientOrder });
      toast.success("Navigation order saved for all workspaces");
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="nav-layout-settings">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-2">
        <p className="text-sm text-[#9FA1A4] max-w-xl">Drag to set the order of the sidebar modules. This order applies globally across every client workspace and the Mission Console.</p>
        <Button data-testid="console-settings-save" onClick={save} disabled={saving || loading || !consoleOrder.length || !clientOrder.length} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full shrink-0">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> Save changes</>}
        </Button>
      </div>
      {loading ? <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#c4c5cc]" /></div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-6">
          <div>
            <h2 className="font-display text-lg font-semibold text-[#06060C] mb-1">Client workspace nav</h2>
            <p className="text-xs text-[#9FA1A4] mb-4">Order shown in every client workspace sidebar.</p>
            <ReorderList testid="client-nav-order" order={clientOrder} setOrder={setClientOrder} meta={CLIENT_META} />
          </div>
          <div>
            <h2 className="font-display text-lg font-semibold text-[#06060C] mb-1">Mission Console nav</h2>
            <p className="text-xs text-[#9FA1A4] mb-4">Order shown in the agency console sidebar.</p>
            <ReorderList testid="console-nav-order" order={consoleOrder} setOrder={setConsoleOrder} meta={CONSOLE_META} />
          </div>
        </div>
      )}
    </div>
  );
}

export default function ConsoleSettings({ onBack, onSaved }) {
  return (
    <main className="px-6 md:px-12 py-12 max-w-3xl mx-auto" data-testid="console-settings-page">
      <button onClick={onBack} className="text-sm text-[#723FFF] hover:text-[#5b2fd6] flex items-center gap-1.5 mb-6"><ArrowLeft className="h-4 w-4" /> Back to console</button>
      <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#06060C] mb-6">Settings</h1>
      <NavLayoutSettings onSaved={onSaved} />
      <CustomCodeCard />
    </main>
  );
}
